/**
 * Niveaux de qualité automatiques (§12, E§3).
 *
 * Mesure les fps sur les premières secondes et fixe un niveau. L'ordre de dégradation
 * est imposé par E§3 et n'est pas négociable :
 *
 *   densité de particules → piétons et véhicules → ombres portées → reflets
 *
 * Le cycle jour/nuit et le juice des actions ne sont **jamais** dégradés : ce sont eux
 * qui portent la sensation. Une ville qui perd ses reflets reste belle ; une ville dont
 * les achats ne réagissent plus n'est plus un jeu.
 */

export type QualityLevel = 'high' | 'medium' | 'low';

export interface QualityProfile {
  readonly level: QualityLevel;
  /** Fraction de particules émises, de 0 à 1. */
  readonly particleDensity: number;
  readonly vehicles: boolean;
  readonly pedestrians: boolean;
  readonly shadows: boolean;
  readonly reflections: boolean;
  /** Toujours vrai : jamais dégradés, quelle que soit la machine. */
  readonly dayNightCycle: true;
  readonly actionJuice: true;
}

export const QUALITY_PROFILES: Readonly<Record<QualityLevel, QualityProfile>> = {
  high: {
    level: 'high',
    particleDensity: 1,
    vehicles: true,
    pedestrians: true,
    shadows: true,
    reflections: true,
    dayNightCycle: true,
    actionJuice: true,
  },
  medium: {
    level: 'medium',
    particleDensity: 0.55,
    vehicles: true,
    pedestrians: false,
    shadows: true,
    reflections: false,
    dayNightCycle: true,
    actionJuice: true,
  },
  low: {
    level: 'low',
    particleDensity: 0.25,
    vehicles: false,
    pedestrians: false,
    shadows: false,
    reflections: false,
    dayNightCycle: true,
    actionJuice: true,
  },
};

/** Seuils de bascule, en images par seconde. */
export const QUALITY_THRESHOLDS = { high: 55, medium: 38 } as const;

/** Durée de la mesure initiale (E§3 : les trois premières secondes). */
export const MEASURE_WINDOW_MS = 3000;

/** Niveau correspondant à une fréquence d'images moyenne. */
export function levelForFps(fps: number): QualityLevel {
  if (fps >= QUALITY_THRESHOLDS.high) return 'high';
  if (fps >= QUALITY_THRESHOLDS.medium) return 'medium';
  return 'low';
}

export interface QualitySample {
  readonly fps: number;
  readonly frames: number;
  readonly elapsedMs: number;
}

/**
 * Compteur de fréquence d'images. Aucune horloge interne : l'appelant fournit `now`,
 * ce qui rend la mesure reproductible en test et insensible à un onglet masqué.
 */
export class FpsMeter {
  private frames = 0;
  private startedAt: number | null = null;
  private lastAt = 0;

  frame(now: number): void {
    this.startedAt ??= now;
    this.frames++;
    this.lastAt = now;
  }

  sample(): QualitySample {
    const elapsedMs = this.startedAt === null ? 0 : this.lastAt - this.startedAt;
    return {
      frames: this.frames,
      elapsedMs,
      /*
        `frames - 1` et non `frames` : n images couvrent n − 1 intervalles. Compter les
        images sur-évalue la fréquence, faiblement mais systématiquement — et un
        instrument de mesure biaisé finit par masquer exactement le problème qu'il
        devait révéler.
      */
      fps: elapsedMs <= 0 ? 0 : ((this.frames - 1) / elapsedMs) * 1000,
    };
  }

  reset(): void {
    this.frames = 0;
    this.startedAt = null;
    this.lastAt = 0;
  }
}

export interface QualityManagerOptions {
  /** Force un niveau et désactive la mesure — utile au harnais de performance. */
  readonly forced?: QualityLevel;
  readonly onChange?: (profile: QualityProfile) => void;
}

/**
 * Choisit et maintient le niveau de qualité.
 *
 * La mesure ne s'arrête pas après les trois premières secondes : elle continue par
 * fenêtres. Un téléphone qui chauffe perd des fps au bout de quelques minutes, et un
 * niveau figé au démarrage laisserait le jeu saccader jusqu'à la fin de la session.
 * En revanche le niveau ne **remonte** jamais tout seul : une oscillation entre deux
 * niveaux serait plus visible que le défaut qu'elle corrige.
 */
export class QualityManager {
  private readonly meter = new FpsMeter();
  private profile: QualityProfile;
  private windowStartedAt: number | null = null;
  private readonly forced: QualityLevel | undefined;
  private readonly onChange: ((profile: QualityProfile) => void) | undefined;

  constructor(options: QualityManagerOptions = {}) {
    this.forced = options.forced;
    this.onChange = options.onChange;
    this.profile = QUALITY_PROFILES[options.forced ?? 'high'];
  }

  getProfile(): QualityProfile {
    return this.profile;
  }

  getFps(): number {
    return this.meter.sample().fps;
  }

  frame(now: number): void {
    if (this.forced !== undefined) return;

    this.meter.frame(now);
    this.windowStartedAt ??= now;

    if (now - this.windowStartedAt < MEASURE_WINDOW_MS) return;

    const measured = levelForFps(this.meter.sample().fps);
    this.meter.reset();
    this.windowStartedAt = now;

    if (RANK[measured] < RANK[this.profile.level]) {
      this.profile = QUALITY_PROFILES[measured];
      this.onChange?.(this.profile);
    }
  }
}

const RANK: Readonly<Record<QualityLevel, number>> = { low: 0, medium: 1, high: 2 };
